import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  HostBinding,
  inject,
  Input,
  model,
  output,
  untracked,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { serializeHtmlCharacterEntitiesFunction } from '../../../functions/html-character-entities.function';
import { CommonModule } from '@angular/common';
import { UiCorePipesModule } from '../../../pipes/ui-core-pipes.module';
import { I18nModule } from '../../../i18n/i18n.module';
import { VirtualKeyboardDirective } from '../../../keyboard/directives/virtual-keyboard.directive';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'soft-input',
  imports: [CommonModule, UiCorePipesModule, I18nModule],
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  hostDirectives: [
    {
      directive: VirtualKeyboardDirective,
      inputs: ['softVirtualKeyboardEnabled', 'softVirtualKeyboardTrigger'],
      outputs: [
        'softVirtualKeyboardEnter',
        'softVirtualKeyboardAppear',
        'softVirtualKeyboardDisappear',
        'softVirtualKeyboardBackdropClick',
        'softVirtualKeyboardInput',
        'softVirtualKeyboardChange:softVirtualKeyboardValueChange',
      ],
    },
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputComponent implements ControlValueAccessor {
  private onChange = (_: string | null | undefined) => {};
  onTouch = () => {};

  readonly _value = model<string | null | undefined>(undefined, {
    alias: 'value',
  });

  readonly inputElement = viewChild<ElementRef<HTMLInputElement>>('inputElement');

  // needed for backwards compatability (many components access .value in templates)
  set value(value: string | null | undefined) {
    this._value.set(value);
  }
  get value(): string | null | undefined {
    return this._value();
  }

  @Input() type: 'text' | 'email' | 'url' | 'tel' = 'text';

  @Input() placeholder?: string | null;

  @Input() @HostBinding('class.readonly') readonly = false;
  @Input() autocomplete?: string;
  @Input() escapeHtml = false;

  readonly valueChange = output<string | null | undefined>();
  readonly blur = output<void>();
  readonly focus = output<void>();

  constructor() {
    inject(VirtualKeyboardDirective).register({
      element: this.inputElement,
      value: computed(() => this._value() ?? null),
      valueChange: (value) => this.setValue(value),
    });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  writeValue(obj: any): void {
    this._value.set(obj);
  }

  setValue(value: string | null): void {
    if (value && this.escapeHtml) value = serializeHtmlCharacterEntitiesFunction(value);
    this._value.set(value);

    this.onChange(this._value());
    this.valueChange.emit(this._value());
  }
}
